import React, { useEffect, useState, useRef } from 'react';
import Header from '../../../components/Header';
import TemporaryInstructionHeader from '../TemporaryOperatingInstruction/TemporaryInstructionHeader';
import UsersTable from './UsersTable';
import { getUsers } from '../../../services/userService';
import CreateUserModal from '../../../components/CreateUserModal';
import { CreateUserProvider } from '../../../components/Providers/CreateUserProvider/CreateUserContext';
import { Plus, X, AlertCircle } from 'lucide-react';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';

interface Department {
  id: number;
  name: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  user_code: string | null;
  role: string | null;
  shift: string | null;
  department_id: number | null;
  department: Department | null;
  profile_photo: string | null;
  user_type?: {
    title: string;
  } | null;
}

const Users = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [successAction, setSuccessAction] = useState<'create' | 'edit' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // PAGINATION STATES
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [searchQuery, setSearchQuery] = useState('');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchUsers = async (page = 1, search = '') => {
    try {
      if (initialLoad) {
        setLoading(true);
      }
      const response = await getUsers(page, search);
      // Handle different response shapes
      const items = response?.data || response || [];
      setUsers(Array.isArray(items) ? items : []);
      // Read meta for pagination
      const meta = response?.meta || response?.data?.meta || null;
      // setCurrentPage(meta?.current_page || page);
      if (meta?.current_page !== currentPage) {
        setCurrentPage(meta?.current_page || page);
      }
      setPageSize(meta?.per_page || 10);
      setTotalPages(meta?.last_page || 1);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  // useEffect(() => {
  //   fetchUsers(currentPage, searchQuery);
  // }, [currentPage, searchQuery]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const delay = searchQuery ? 150 : 0;

    debounceRef.current = setTimeout(() => {
      fetchUsers(currentPage, searchQuery);
    }, delay);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [currentPage, searchQuery]);

  const [editData, setEditData] = useState<User | null>(null);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <Header title="Users" />
      <div className="flex-1 overflow-auto p-6 bg-white rounded-xl shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <TemporaryInstructionHeader
            selectedView="Users"
            onSearch={(query) => {
              setCurrentPage(1);
              setSearchQuery(query);
            }}
          />
          <button
            className="flex items-center gap-2 bg-[#e84545] text-white px-4 py-2 rounded-md text-sm font-medium hover:opacity-90"
            onClick={() => setOpenCreateUser(true)}
          >
            <Plus size={16} /> New User
          </button>
        </div>

        {/* Error Toast */}
        {errorMessage && (
          <div className="fixed bottom-6 right-6 z-[100] animate-slide-up">
            <div className="bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg flex items-start gap-3 min-w-[320px] max-w-md">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold mb-1">Error</h3>
                <p className="text-sm">{errorMessage}</p>
              </div>
              <button onClick={() => setErrorMessage(null)} className="flex-shrink-0 hover:bg-red-600 rounded p-1 transition-colors">
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Pass data + state down to table */}
        <UsersTable
          users={users}
          loading={loading}
          error={error}
          onRefresh={() => fetchUsers(currentPage, searchQuery)}
          onEdit={(user) => {
            setEditData(user);
            setOpenCreateUser(true);
          }}
          onError={(message) => {
            setErrorMessage(message);
            setTimeout(() => setErrorMessage(null), 5000);
          }}
          // PASS PAGINATION PROPS
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />

        {/* Create User Modal */}
        <CreateUserProvider>
          <CreateUserModal
            isOpen={openCreateUser}
            onClose={() => {
              setOpenCreateUser(false);
              setEditData(null);
            }}
            onUserCreated={(action) => {
              setOpenCreateUser(false);
              fetchUsers(currentPage, searchQuery);
              setSuccessAction(action);
              setSuccessModal(true);
            }}
            onError={(message) => {
              setErrorMessage(message);
              setTimeout(() => setErrorMessage(null), 5000);
            }}
            editData={editData}
          />
        </CreateUserProvider>
      </div>

      <SuccessModal
        isOpen={successModal}
        onClose={() => setSuccessModal(false)}
        title={successAction === 'edit' ? 'User updated' : 'User created'}
        message={successAction === 'edit' ? 'User has been updated successfully.' : 'User has been created successfully.'}
        buttonText="OK"
        onButtonClick={() => setSuccessModal(false)}
      />
    </div>
  );
};

export default Users;
